import { useMutation } from '@apollo/client';
import { faChevronDown, faFileCode } from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { getTemplateFormat } from './OutputSettings.tsx';
import { GENERATE_TEMPLATE_FROM_XSD } from './queries.ts';
import TemplateAiAssistant from './TemplateAiAssistant.tsx';
import TemplateCodeEditor from './TemplateCodeEditor.tsx';
import {
    addTargetFields,
    BUILTIN_TEMPLATE_VARIABLES,
    collectCollectionSources,
    collectTemplateVariables,
} from './templateVariables.ts';
import Alert from '../../../core/components/Alert.tsx';
import Button from '../../../core/components/Button.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { THINKING } from '../../../core/components/worki/Pose.tsx';
import Worki from '../../../core/components/worki/Worki.tsx';
import WorkiThinking from '../../../core/components/worki/WorkiThinking.tsx';
import FieldsConfig from '../../../document/components/FieldsConfig.tsx';
import type { OutputMapperConfig } from '../OutputMapperEditor/types.ts';

const ChevronDownIcon = withIcon(faChevronDown);
const XsdIcon = withIcon(faFileCode);

interface TemplatePanelProps {
    config: OutputMapperConfig | null;
    onConfigChange: (config: OutputMapperConfig) => void;
    /** Open the Target fields tab with this target (`name` or `collection[].name`) selected. */
    onOpenTargetField: (target: string) => void;
}

/** A seed for the template editor; `xsdKey` present means the seed also (re)selects the XSD. */
interface TemplateSeed {
    template: string;
    xsdKey?: string | null;
    /** The starter's values no target field carries yet, offered to the AI to add. */
    label?: string;
    unbound?: string[];
}

/**
 * Template authoring tab for the `template` output type, in the field configuration's look: "Create with AI" /
 * "Edit with AI" (from an example file and/or an instruction), then the template section with the starters of the
 * selected format, "Generate from XSD" for XML templates, and a code editor where `/` inserts the mapping's
 * variables (including the field mappings nested in `map_array` actions, offered as loop-scoped variables).
 */
const TemplatePanel = ({ config, onConfigChange, onOpenTargetField }: TemplatePanelProps) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();
    const [pendingSeed, setPendingSeed] = useState<TemplateSeed | null>(null);
    // while the AI writes the template the editor is locked, so no edit is lost when the result replaces it
    const [aiLoading, setAiLoading] = useState(false);
    // the config before the last AI suggestion, so Undo also removes the target fields it added
    const [beforeAi, setBeforeAi] = useState<OutputMapperConfig | null>(null);
    // values of the format just started from that no target field carries yet
    const [missingTargets, setMissingTargets] = useState<{ label: string; values: string[] } | null>(null);
    // an instruction handed to the AI assistant (e.g. "add the missing target fields"); id makes repeats count
    const [presetInstruction, setPresetInstruction] = useState<{ text: string; id: number } | null>(null);
    const [generateTemplate, { loading: generating }] = useMutation(GENERATE_TEMPLATE_FROM_XSD);

    if (!config) return null;

    const template = config.template ?? '';
    const format = getTemplateFormat(config);
    const templateDefaults = (config.template_defaults ?? []).filter(
        (entry) => entry.template && (entry.template_format ?? 'xml') === format
    );
    const targetVariables = collectTemplateVariables(config.output_mapping ?? []);
    const targets = targetVariables.map((variable) => variable.value);
    const variables = [...targetVariables, ...BUILTIN_TEMPLATE_VARIABLES];
    const xsdKey = format === 'xml' ? (config.template_xsd_key ?? null) : null;

    const setTemplate = (next: string) => onConfigChange({ ...config, template: next });

    const applySeed = (seed: TemplateSeed) => {
        setMissingTargets(seed.unbound?.length ? { label: seed.label ?? '', values: seed.unbound } : null);
        onConfigChange({
            ...config,
            template: seed.template,
            ...(seed.xsdKey !== undefined ? { template_xsd_key: seed.xsdKey } : {}),
        });
    };

    const requestSeed = (seed: TemplateSeed) => {
        if (template.trim()) {
            setPendingSeed(seed);
            return;
        }
        applySeed(seed);
    };

    const pickDefault = (key: string) => {
        const starter = templateDefaults.find((entry) => entry.key === key);
        if (!starter) return;
        // Seeding a starter also selects its matching XSD (or clears it when the starter has none),
        // so validation follows the chosen dialect without a separate manual pick.
        requestSeed({
            template: starter.template,
            xsdKey: starter.xsd_key ?? null,
            label: starter.label,
            unbound: starter.unbound ?? [],
        });
    };

    const handleGenerateFromXsd = async () => {
        if (!xsdKey) return;
        try {
            const result = await generateTemplate({
                variables: { xsdKey, xsdContent: config.template_xsd ?? null, targets },
            });
            const payload = result.data?.generateTemplateFromXsd;
            if (payload?.success && typeof payload.template === 'string') {
                requestSeed({ template: payload.template });
            } else {
                publishToast({
                    description: payload?.error || t('outputMapper.template.generateFailed', 'Generation failed'),
                    status: 'error',
                });
            }
        } catch (error) {
            console.error(error);
            publishToast({
                description: t('outputMapper.template.generateFailed', 'Generation failed'),
                status: 'error',
            });
        }
    };

    return (
        <div className="flex h-full min-h-0 flex-col gap-6 overflow-auto pb-2">
            <TemplateAiAssistant
                template={template}
                format={format}
                targets={targets}
                inputTypes={config.input_types ?? []}
                collectionSources={collectCollectionSources(config.output_mapping ?? [])}
                onApply={(nextTemplate, newTargetFields) => {
                    setBeforeAi(config);
                    onConfigChange({
                        ...config,
                        template: nextTemplate,
                        output_mapping: addTargetFields(config.output_mapping ?? [], newTargetFields),
                    });
                }}
                onUndo={() => {
                    if (beforeAi) onConfigChange(beforeAi);
                    setBeforeAi(null);
                }}
                onLoadingChange={setAiLoading}
                presetInstruction={presetInstruction}
                onFormatChange={(nextFormat) => {
                    onConfigChange({ ...config, template_format: nextFormat });
                    publishToast({
                        description: t('outputMapper.templateAi.formatSwitched', {
                            defaultValue: 'Template format set to {{format}} to match the example file.',
                            format: nextFormat.toUpperCase(),
                        }),
                        status: 'info',
                    });
                }}
            />
            <FieldsConfig.Section
                title={t('outputMapper.template.title', 'Template')}
                description={t('outputMapper.template.description', {
                    defaultValue: 'Jinja template of the {{format}} file. Each record renders one file.',
                    format: format.toUpperCase(),
                })}
                aside={
                    <>
                        {xsdKey && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="flex items-center gap-2"
                                onClick={handleGenerateFromXsd}
                                disabled={generating || aiLoading}
                                loading={generating}
                            >
                                <XsdIcon />
                                {t('outputMapper.template.generateFromXsd', 'Generate from XSD')}
                            </Button>
                        )}
                        {templateDefaults.length > 0 && (
                            <DropdownMenu>
                                <DropdownMenu.Trigger asChild disabled={aiLoading}>
                                    <Button type="button" variant="outline" className="flex items-center gap-2">
                                        {t('outputMapper.template.insertDefault', 'Start from a format')}
                                        <ChevronDownIcon className="text-xs" />
                                    </Button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content align="end">
                                    {(['standard', 'generic'] as const).map((group, index) => {
                                        const entries = templateDefaults.filter(
                                            (entry) => (entry.group ?? 'generic') === group
                                        );
                                        if (entries.length === 0) return null;
                                        return (
                                            <React.Fragment key={group}>
                                                {index > 0 && <DropdownMenu.Separator />}
                                                <DropdownMenu.Label>
                                                    {group === 'standard'
                                                        ? t('outputMapper.template.standardFormats', 'Standard formats')
                                                        : t('outputMapper.template.genericFormats', 'Custom skeletons')}
                                                </DropdownMenu.Label>
                                                {entries.map((entry) => (
                                                    <DropdownMenu.Item
                                                        key={entry.key}
                                                        onClick={() => pickDefault(entry.key)}
                                                    >
                                                        {entry.label}
                                                    </DropdownMenu.Item>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                    <DropdownMenu.Separator />
                                    <DropdownMenu.Item onClick={() => requestSeed({ template: '' })}>
                                        {t('outputMapper.template.blank', 'Blank template')}
                                    </DropdownMenu.Item>
                                </DropdownMenu.Content>
                            </DropdownMenu>
                        )}
                    </>
                }
            >
                {missingTargets && (
                    <Alert severity="info">
                        <div className="flex items-start justify-between gap-3">
                            <span>
                                {t('outputMapper.template.missingTargets', {
                                    defaultValue:
                                        '{{format}} uses values that have no target field yet, so they render empty: {{values}}',
                                    format: missingTargets.label,
                                    values: missingTargets.values.join(', '),
                                })}
                            </span>
                            <div className="flex flex-none items-center gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={aiLoading}
                                    onClick={() => {
                                        setPresetInstruction({
                                            text: t('outputMapper.template.addMissingInstruction', {
                                                defaultValue:
                                                    'Add target fields for these values and use them in the template: {{values}}',
                                                values: missingTargets.values.join(', '),
                                            }),
                                            id: Date.now(),
                                        });
                                        setMissingTargets(null);
                                    }}
                                >
                                    {t('outputMapper.template.addMissingWithAi', 'Add with AI')}
                                </Button>
                                <Button type="button" variant="ghost" onClick={() => setMissingTargets(null)}>
                                    {t('outputMapper.template.dismiss', 'Dismiss')}
                                </Button>
                            </div>
                        </div>
                    </Alert>
                )}
                <TemplateCodeEditor
                    value={template}
                    onValueChange={setTemplate}
                    variables={variables}
                    format={format}
                    onOpenTargetField={onOpenTargetField}
                    locked={aiLoading}
                    overlay={
                        <div className="flex flex-col items-center gap-3">
                            <Worki pose={THINKING} size={96} trackMouse="none" showGlow={false} />
                            <WorkiThinking
                                state="working"
                                label={t('outputMapper.templateAi.writing', 'Worki is writing the template…')}
                            />
                        </div>
                    }
                    placeholder={t(
                        'outputMapper.template.placeholder',
                        'Write the output template here, create it with AI, or start from a default template.'
                    )}
                />
            </FieldsConfig.Section>

            {pendingSeed && (
                <ConfirmModal
                    visible
                    title={t('outputMapper.template.confirmReplaceTitle', 'Replace template?')}
                    labelConfirm={t('outputMapper.template.confirmReplaceAction', 'Replace template')}
                    onCancel={() => setPendingSeed(null)}
                    onConfirm={() => {
                        applySeed(pendingSeed);
                        setPendingSeed(null);
                    }}
                >
                    <div className="text-sm text-content-strong">
                        {t(
                            'outputMapper.template.confirmReplaceBody',
                            'Inserting a default template replaces the current template content.'
                        )}
                    </div>
                </ConfirmModal>
            )}
        </div>
    );
};

export default TemplatePanel;
